/**
 * Asset spec and element loading for the twin. Specs come from the runtime
 * registry (registerAssetSpec: the dive's parametric antennas, placed
 * antennas), /data/twin/assets/<id>.json (zod-validated) or, for
 * `noaa:<antennaId>`, from the NOAA antenna record through the parametric spec
 * builder, in that order. Elements come
 * from /data/elements/<id>.json with their history re-dated to now, as the old
 * DataLoader did. Load failures throw with the URL and status; callers (the
 * TwinRuntime, the harness) put them in useTwin.setLoadError.
 */
import type { Antenna, Mission } from '@/features/loader/types';
import { antennaFileForNetwork, missionFileForNetwork } from '@/features/loader/data/staticData';
import { assetSpecForMission, missionTemplateFor } from './missionSpec';
import { clockNow } from '@/state/store';
import type { AssetSpec, Element } from '../types';
import { assetSpecForAntenna } from './antennaSpec';
import { normaliseHistoryDates } from './history';
import { parseAssetSpec, parseElement } from './schema';
import { noaaEntry } from '@/features/loader/catalog/noaaCatalogue';
import { noaaReferenceElement } from './noaaElement';
import { facilityById, type FacilityMap } from '@/features/catalogue/facilities';
import { assetSpecForFacility, facilityElement } from './facilitySpec';
import { isRfims, rfimsSite } from '@/features/rfims/registry';
import { rfimsElement } from '@/features/rfims/element';
import { rfimsAssetSpec } from '@/features/rfims/siteSpec';

export {
  ANTENNA_POV,
  NOAA_ASSET_PREFIX,
  RECEIVE_CHAIN,
  RF_ANTENNA_IDS,
  antennaPedestal,
  antennaRadome,
  antennaRig,
  antennaSignalPaths,
  antennaSubviews,
  assetSpecForAntenna,
  antennaAssetId,
  proceduralSpecForAntenna,
  RFIMS_LIVE_CARD_IDS,
  rfAntennaBindings,
  rfimsBindings,
  rfimsLiveBindings,
} from './antennaSpec';
export { assetName, type AssetName } from './assetName';
export {
  DEFAULT_HISTORY_INTERVAL_MS,
  historyIntervalMs,
  medianIntervalMs,
  normaliseHistoryDates,
  normaliseItemHistory,
  parseHistoryDate,
} from './history';
export {
  assetSpecSchema,
  cameraPoseSchema,
  elementSchema,
  parseAssetSpec,
  parseElement,
  proceduralSchema,
} from './schema';

export const ASSET_SPEC_BASE = '/data/twin/assets';
export const ELEMENT_BASE = '/data/elements';
export const NOAA_ANTENNAS_URL = '/data/loader/antennas-noaa.json';

/**
 * Assets of the tree that ship without an element file (appendix B: the
 * weather centre's panel stays empty). They load as an element with no
 * subsystems instead of failing.
 */
export const ELEMENTS_WITHOUT_JSON = new Set(['weather_center', 'generic_satellite']);

async function fetchJson(url: string, what: string): Promise<unknown> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`${what}: HTTP ${res.status} for ${url}`);
  return res.json();
}

const specCache = new Map<string, Promise<AssetSpec>>();
const antennaCache = new Map<string, Promise<Antenna[]>>();
/** Specs registered at runtime; loadAssetSpec answers from here before any cache or fetch. */
const registry = new Map<string, AssetSpec>();
const registeredAntennas = new Map<string, Antenna>();
const MISSION_SELECTION_CACHE = 'gem-dt:mission-twin-references:v1';
type MissionIdentity = Pick<Mission, 'id' | 'name' | 'noradId'>;
function rememberedMission(network: string, id: string): MissionIdentity | undefined {
  if (typeof sessionStorage === 'undefined') return undefined;
  try {
    const rows = JSON.parse(sessionStorage.getItem(MISSION_SELECTION_CACHE) ?? '[]');
    if (!Array.isArray(rows)) return undefined;
    const row = rows.find(r => r?.network === network && r?.mission?.id === id);
    const m = row?.mission;
    if (m && typeof m.name === 'string' && (m.noradId == null || ['string', 'number'].includes(typeof m.noradId))) return m;
  } catch { /* A damaged or unavailable session cache must not block the twin. */ }
  return undefined;
}
function rememberMission(network: string, mission: MissionIdentity): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(MISSION_SELECTION_CACHE) ?? '[]');
    const rows = Array.isArray(parsed) ? parsed : [];
    sessionStorage.setItem(MISSION_SELECTION_CACHE, JSON.stringify([
      { network, mission: { id: mission.id, name: mission.name, noradId: mission.noradId } },
      ...rows.filter(r => r?.network !== network || r?.mission?.id !== mission.id).slice(0, 127),
    ]));
  } catch { /* Storage may be disabled. Runtime registration still works. */ }
}

/**
 * Registers a spec built at runtime so loadAssetSpec(assetId) resolves it
 * without a fetch. The spec is validated with the asset schema (throws with
 * the issues when malformed) and its id must equal assetId. A later
 * registration under the same id replaces it; a registered id shadows a
 * /data/twin/assets file of the same name (loadAssetSpec asks the registry
 * before its cache). Returns the validated spec as stored.
 */
export function registerAssetSpec(assetId: string, spec: AssetSpec): AssetSpec {
  if (spec.id !== assetId)
    throw new Error(`registerAssetSpec: spec id "${spec.id}" does not match "${assetId}"`);
  const parsed = parseAssetSpec(spec, `registered asset spec ${assetId}`);
  registry.set(assetId, parsed);
  return parsed;
}

/** Removes a registered spec and any cached load of that id; true when one was registered. */
export function unregisterAssetSpec(assetId: string): boolean {
  specCache.delete(assetId);
  registeredAntennas.delete(assetId);
  return registry.delete(assetId);
}

/** The spec registered under assetId, if any. */
export function registeredAssetSpec(assetId: string): AssetSpec | undefined {
  return registry.get(assetId);
}

async function loadAntennas(network: string): Promise<Antenna[]> {
  const file = antennaFileForNetwork(network);
  if (!file) throw new Error(`Unknown antenna network: ${network}`);
  const url = `/data/loader/${file}`;
  let promise = antennaCache.get(network);
  if (promise) return promise;
  promise = fetchJson(url, `${network} antennas`).then((json) => {
    const list = (json as { antennas?: Antenna[] }).antennas;
    if (!Array.isArray(list)) throw new Error(`${url} has no antennas array`);
    return list;
  });
  antennaCache.set(network, promise);
  promise.catch(() => antennaCache.delete(network));
  return promise;
}

/** Build from the current record, including edits/placed antennas absent from bundled JSON. */
export function registerAntennaTwin(antenna: Antenna, network: string): string {
  const mapped = (antenna as Antenna & { twin?: { assetId?: string } }).twin?.assetId;
  if (mapped) return mapped;
  const spec = assetSpecForAntenna(antenna, network);
  registerAssetSpec(spec.id, spec);
  registeredAntennas.set(spec.id, antenna);
  return spec.id;
}

export async function registerMissionTwin(
  mission: Pick<Mission, 'id' | 'name' | 'noradId'>,
  network: string,
): Promise<string> {
  rememberMission(network, mission);
  const templateId = missionTemplateFor(mission);
  const template = templateId ? await loadAssetSpec(templateId) : undefined;
  const spec = assetSpecForMission(mission, network, template);
  registerAssetSpec(spec.id, spec);
  return spec.id;
}

export async function loadAssetSpec(assetId: string): Promise<AssetSpec> {
  const registered = registry.get(assetId);
  if (registered) return registered;
  const cached = specCache.get(assetId);
  if (cached) return cached;
  const promise = (async () => {
    if (isRfims(assetId)) {
      const canonical = rfimsSite(assetId)!.id;
      let map: FacilityMap | null = null;
      try { map = await fetchJson(`/data/sites/${canonical}-v2.json`, 'RFIMS surroundings') as FacilityMap; } catch { /* Location remains usable when mapping is unavailable. */ }
      return rfimsAssetSpec(canonical, map);
    }
    if (assetId.startsWith('facility:')) {
      const facility = facilityById(assetId.slice('facility:'.length));
      if (!facility) throw new Error(`Unknown facility: ${assetId}`);
      const networks = [...new Set(facility.members.map((m) => m.network))];
      const lists = await Promise.all(
        networks.map(async (network) => [network, await loadAntennas(network)] as const),
      );
      const antennas = facility.members.map((m) => ({
        network: m.network,
        antenna: lists.find(([network]) => network === m.network)?.[1].find((a) => a.id === m.antennaId),
      }));
      if (antennas.some((a) => !a.antenna))
        throw new Error(`Facility ${facility.name} has an unresolved antenna membership`);
      let map: FacilityMap | null = null;
      if (facility.mapUrl) {
        try {
          map = (await fetchJson(facility.mapUrl, 'Facility map')) as FacilityMap;
        } catch {
          /* Model still shows catalogue terminals. */
        }
      }
      return assetSpecForFacility(facility, antennas as { network: string; antenna: Antenna }[], map);
    }
    if (assetId.startsWith('mission:')) {
      const [, encodedNetwork, encodedId] = assetId.split(':');
      if (!encodedNetwork || !encodedId) throw new Error(`Invalid mission asset id: ${assetId}`);
      const network = decodeURIComponent(encodedNetwork);
      const id = decodeURIComponent(encodedId);
      const file = missionFileForNetwork(network);
      const data = file
        ? ((await fetchJson(`/data/loader/${file}`, 'Mission twin')) as { missions: Mission[] })
        : null;
      const mission = data?.missions.find((m) => m.id === id) ?? rememberedMission(network, id) ?? {
        id, name: id, noradId: /^(station|gnss|sentinel|oneweb|starlink|satnogs|aws)_(\d+)$/.exec(id)?.[2],
      };
      const templateId = missionTemplateFor(mission);
      return assetSpecForMission(mission, network, templateId ? await loadAssetSpec(templateId) : undefined);
    }
    if (assetId.includes(':')) {
      const separator = assetId.indexOf(':');
      const network = assetId.slice(0, separator);
      const antennaId = assetId.slice(separator + 1);
      const antenna = (await loadAntennas(network)).find((a) => a.id === antennaId);
      if (!antenna) throw new Error(`asset spec ${assetId}: no ${network} antenna ${antennaId}`);
      return assetSpecForAntenna(antenna, network);
    }
    const url = `${ASSET_SPEC_BASE}/${encodeURIComponent(assetId)}.json`;
    const spec = parseAssetSpec(await fetchJson(url, `asset spec ${assetId}`), url);
    if (spec.id !== assetId)
      throw new Error(`asset spec ${url}: id "${spec.id}" does not match the file name`);
    return spec;
  })();
  specCache.set(assetId, promise);
  promise.catch(() => specCache.delete(assetId));
  return promise;
}

export async function loadElement(elementId: string): Promise<Element> {
  if (isRfims(elementId)) return rfimsElement(elementId);
  if (elementId.startsWith('facility:')) {
    const facility = facilityById(elementId.slice('facility:'.length));
    if (!facility) throw new Error(`Unknown facility: ${elementId}`);
    return facilityElement(facility, await loadAssetSpec(elementId));
  }
  if (elementId.startsWith('antenna_reference:')) {
    const base = await loadElement('rf_antenna');
    const [, network, ...id] = elementId.split(':');
    const antenna =
      registeredAntennas.get(`${network}:${id.join(':')}`) ??
      (antennaFileForNetwork(network)
        ? (await loadAntennas(network)).find((a) => a.id === id.join(':'))
        : undefined);
    const stripReadings = (subs: Element['subsystems']): Element['subsystems'] =>
      subs.map((s) => ({
        id: s.id,
        label: s.label,
        description: 'Representative antenna equipment; no live telemetry assigned.',
        modelOnly: true,
        data: [],
        ...(s.childs ? { childs: stripReadings(s.childs) } : {}),
      }));
    const subsystems = stripReadings(base.subsystems);
    if (antenna && subsystems[0]) {
      const spec = assetSpecForAntenna(antenna, network);
      subsystems[0].reference = {
        sourceTitle: spec.site?.sourceTitle ?? 'Catalogue map reference',
        sourceUrl:
          spec.site?.sourceUrl ??
          `https://www.openstreetmap.org/#map=18/${antenna.location.lat}/${antenna.location.lon}`,
        checkedAt: '2026-10-15',
        note: spec.modelNote,
        values: [
          {
            label: 'Catalogue diameter',
            value: antenna.capabilities.diameterM
              ? `${antenna.capabilities.diameterM} m`
              : 'Unspecified; illustrative 4 m model',
          },
          { label: 'Bands', value: antenna.capabilities.bands.join(', ') },
          { label: 'Site', value: antenna.location.name ?? 'Catalogue coordinate' },
        ],
      };
    }
    return { id: elementId, type: 'Antenna reference', subsystems };
  }
  if (elementId.startsWith('noaa_reference:')) {
    const entry = noaaEntry(elementId.slice('noaa_reference:'.length));
    if (!entry) throw new Error(`Unknown NOAA reference ${elementId}`);
    const spec = entry.template ? await loadAssetSpec(entry.template) : undefined;
    return noaaReferenceElement(entry, spec ? await loadElement(spec.elementId) : undefined);
  }
  if (ELEMENTS_WITHOUT_JSON.has(elementId)) return { id: elementId, subsystems: [] };
  const url = `${ELEMENT_BASE}/${encodeURIComponent(elementId)}.json`;
  const res = await fetch(url, { credentials: 'same-origin' });
  if (res.status === 404 && ELEMENTS_WITHOUT_JSON.has(elementId)) {
    console.warn(`[twin] element ${elementId} has no JSON (${url}); loading it with no subsystems`);
    return { id: elementId, subsystems: [] };
  }
  if (!res.ok) throw new Error(`element ${elementId}: HTTP ${res.status} for ${url}`);
  const element = parseElement(await res.json(), url);
  return normaliseHistoryDates(element, clockNow());
}

/** Drops cached specs (the harness uses it when switching assets after an edit). */
export function clearAssetSpecCache(): void {
  specCache.clear();
  antennaCache.clear();
}
